import { useEffect, useRef } from 'react';

import { cn } from '~/lib/cn';

import type { FiltersPanelRef } from './ArtistFilterPanel';
import { ArtistFilterPanel } from './ArtistFilterPanel';
import type { ArtistFilterProps } from './types';

const BACKDROP_CLASSES = {
  hidden: 'opacity-none',
  visible: 'opacity-full',
};

export const ArtistFilter = (props: ArtistFilterProps) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const filtersPanelRef = useRef<FiltersPanelRef>(null);

  const handleClose = () => {
    if (!backdropRef.current || !filtersPanelRef.current) {
      return;
    }

    backdropRef.current.addEventListener('transitionend', props.onClose);
    // Animate the backdrop out of view as it closes.
    backdropRef.current.classList.add(BACKDROP_CLASSES.hidden);
    backdropRef.current.classList.remove(BACKDROP_CLASSES.visible);
    filtersPanelRef.current.closePanel();

    return () => {
      backdropRef.current?.removeEventListener('transitionend', props.onClose);
    };
  };

  // Animate the backdrop into view when it mounts.
  useEffect(() => {
    backdropRef.current?.classList.add(BACKDROP_CLASSES.visible);
    backdropRef.current?.classList.remove(BACKDROP_CLASSES.hidden);
  }, []);

  return (
    <div className="fixed inset-0 z-10">
      <div
        ref={backdropRef}
        className={cn(
          'size-full backdrop-blur transition-all duration-300 ease-out',
          BACKDROP_CLASSES.hidden,
        )}
        data-testid="artist-filter-panel-blur-overlay"
        aria-hidden="true"
        onClick={handleClose}
      />
      <ArtistFilterPanel
        {...{ ...props, onClose: handleClose, ref: filtersPanelRef }}
      />
    </div>
  );
};

ArtistFilter.displayName = 'ArtistFilter';
